import { omit } from 'lodash-es';
// import { getAllCountryCodesByRegions } from '@intelligence/v3/utils/region';
import { MAX_ROWS_LIMIT } from '../utils';
import { shouldUseSSE } from '../utils/sse';
// import { DataMethod } from '@intelligence/v3/enums/download';
import { DOWNLOAD_CFG_KEY } from '@intelligence/v3/constants';
import commonSetting from '../configs/commonSetting';
// import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import { generateSortKey } from '@intelligence/v3/utils/common';

const groupByGenerators = [
  {
    formModelKey: DOWNLOAD_CFG_KEY.DATE_METHOD,
    groupByKey: 'date',
    condition: (payload: string) => payload !== 'aggregated',
  },
  {
    formModelKey: DOWNLOAD_CFG_KEY.REGION_METHOD,
    groupByKey: 'region',
    condition: (payload: string) => payload === 'region',
  },
  {
    formModelKey: DOWNLOAD_CFG_KEY.REGION_METHOD,
    groupByKey: 'market',
    condition: (payload: string) => payload === 'market',
  },
];

const groupByFormModelKeys = groupByGenerators.map((generator) => generator?.formModelKey);

const getFileName = (params: any) => {
  const startDate = params?.start_date || '';
  const endDate = params?.end_date || '';
  const newStyleDownloadFileName = params?.new_style_donwload_file_name || '';
  if (!!startDate && !!endDate) {
    return `${newStyleDownloadFileName}_${startDate}_${endDate}`;
  }
  return `${newStyleDownloadFileName}`;
};

export const getDownloadCategoryOrderBy = (genreSource: string, category: string): string[] => {
  if (!category) {
    return [];
  }
  if (category === 'operation') {
    return {
      group_by: ['market'],
      metrics: [
        "mscience__digitalRevenue",
        "npd__revenue",
      ],
      order_by: [
        "market desc",
      ],
      top: {},
      filter: {},
      use_sse_page: {},
    }
  } else if (category === 'games') {
    return {
      group_by: ['game_id'],
      metrics: [
        "mscience__digitalRevenue",
        // "mscience__digitalRevenue_ratio",
        "npd__revenue",
        // "npd__revenue_ratio",
        "newzoo__mau",
        // "newzoo__mau_ratio",
      ],
      order_by: [
        "mscience__digitalRevenue desc",
      ],
      top: {},
      filter: {},
      use_sse_page: {},
    }
  } else if (category === 'main_genres') {
    return {
      group_by: ['genre', 'sub_genre'],
      metrics: [
        "mscience__digitalRevenue",
        "npd__revenue",
      ],
      order_by: [
        "mscience__digitalRevenue desc",
      ],
      top: {},
      filter: {},
      use_sse_page: {},
    }
  } else if (category === 'companies') {
    return {
      group_by: ['company_id'],
      metrics: [
        "mscience__digitalRevenue",
        "npd__revenue",
      ],
      order_by: [
        "mscience__digitalRevenue desc",
      ],
      top: {},
      filter: {},
      use_sse_page: {},
    }
  }
};

/**
 * 获取下载的markets入参
 * @param params 参数对象
 * @return 下载入参的国家列表
 */
const getDownloadMarkets = (params?: IRecord): string[] => {
  const market = params?.market || 'us';
  return [market];
};

const getDownloadApiParams = <T extends IRecord = any>(params: T): T => {
  if (!params) return {} as unknown as T;
  const filter = (() => {
    const ieggGenres = params?.iegg_genre_req || [];
    return {
      iegg_genre_req: ieggGenres,
    };
  })();

  const groupBys = (() => {
    const groupBys: string[] = [];

    // 处理 Time Settings 和 Region Settings
    groupByGenerators.forEach((generator) => {
      const formModelKey = generator?.formModelKey;
      const groupByKey = generator?.groupByKey;
      const condition = generator?.condition;
      if (!!condition?.(params?.[formModelKey])) {
        groupBys.push(groupByKey);
      }
    });

    return groupBys;
  })();

  const orderBys = (() => {
    // const sortKey = params?.order_by || 'digital_revenue';
    const sortKey = 'sensortower__mau';
    const sortType = params?.order || 'desc';

    const sortBy = generateSortKey(sortKey, sortType);

    if (!sortBy) {
      return [];
    }

    return [sortBy];
  })();

  const apiParams = {
    ...omit(params, [
      ...groupByFormModelKeys,
      DOWNLOAD_CFG_KEY.DATA_METHOD,
      DOWNLOAD_CFG_KEY.SELECT_TOP,
      'downlaod_date_method',
      'entity_type',
      'dataSource',
      'genre',
      'iegg_genre_req',
      'mainGenreList',
      'market',
      'data_tag',
      'order',
      'order_by',
      'FileFormat',
      'file_name',
      'file_format',
      'new_style_donwload_file_name',
      'ratio_show',
    ]),
    filter,
    // 配置所有指标
    metrics: [
      'mscience__digitalRevenue',
      'mscience__digitalRevenue_ratio',
      'npd__revenue',
      'npd__revenue_ratio',
      'newzoo__mau',
      'newzoo__mau_ratio',
      'mscience__digitalRevenue_share',
      'npd__revenue_share',
    ],
    markets: getDownloadMarkets(params),
    group_by: groupBys,
    order_by: orderBys,
    page: 1,
    page_size: 10000,
    top: {
      group_by: [],
      order_by: orderBys,
      size: params?.select_top,
    },
    use_custom_fields: [],
    use_sse_page: {
      max_size: MAX_ROWS_LIMIT,
    },
  } as unknown as T;

  if (params?.[DOWNLOAD_CFG_KEY.DATA_METHOD] === 'selectAll') {
    delete apiParams.top;
  }

  return apiParams;
};

export default (params: any) => {
  const apiParams = getDownloadApiParams(params);
  // 检查是否启用 SSE 模式
  const useSSE = shouldUseSSE(apiParams);

  return {
    ...commonSetting,
    downloadMode: 'commonConf',
    downloadConfigKey: 'marketProfileStatsNew',
    downloadFetchApi: '/api/v1/intelligence_pc/profile/market/metric_list',
    downloadApiParams: apiParams,
    isUsingNewMethod: false,
    // SSE 模式下不需要 fetchOnlyOnce，因为数据是流式传输的
    fetchOnlyOnce: !useSSE,
    dataKey: undefined,
    $$fileName: getFileName(params),
    regionOptions: params?.regionOptions,
  };
};
